import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
  Building2,
  Users,
  Sparkles,
  X,
  Plus,
  MapPin,
  Calendar,
  Loader2,
  ArrowUp,
  RotateCcw,
  ChevronUp,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { agendamentosService, type ValidarPlanilhaResult } from '../../services/agendamentos.service';
import { colaboradoresService, type CreateColaboradorDto } from '../../services/colaboradores.service';
import { cargosService } from '../../services/cargos.service';
import {
  clinicasService,
  type EstadoItem,
  type CidadeItem,
  type ClinicaItem,
} from '../../services/clinicas.service';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { formatCPF } from '../../utils/formatters';
import { formatDateBR } from '../../utils/dateUtils';
import { validateExcelFile, sanitizeSpreadsheetRows } from '../../utils/fileSecurity';
import { limparRascunhoAgendamento } from '../../utils/agendamentoStorage';
import type { ValidarPlanilhaRowContract } from '@sstexames/shared-types';

interface UploadLotePlanilhaProps {
  onImportar: (colaboradores: ValidarPlanilhaResult['validRows']) => void;
  onVoltar: () => void;
}

const formatDisplayDate = (dateStr?: string | null): string => {
  if (!dateStr || typeof dateStr !== 'string') return '-';
  const clean = dateStr.trim();
  if (!clean) return '-';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(clean)) return clean;
  if (/^\d{4}-\d{2}-\d{2}/.test(clean)) {
    const br = formatDateBR(clean);
    return br !== 'N/A' ? br : clean;
  }
  const br = formatDateBR(clean);
  return br !== 'N/A' ? br : clean;
};

const formatDateForAPI = (dateStr?: string | null): string | undefined => {
  if (!dateStr || typeof dateStr !== 'string') return undefined;
  const clean = dateStr.trim();
  if (!clean) return undefined;
  if (clean.match(/^\d{4}-\d{2}-\d{2}$/)) return clean;
  if (clean.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [dia, mes, ano] = clean.split('/');
    return `${ano}-${mes}-${dia}`;
  }
  if (clean.includes('T')) {
    return clean.split('T')[0];
  }
  return undefined;
};

const getExameNome = (exameId?: string | number): string => {
  const idStr = String(exameId || '1');
  switch (idStr) {
    case '1':
      return 'Admissional';
    case '2':
      return 'Demissional';
    case '3':
      return 'Periódico';
    case '4':
      return 'Retorno ao Trabalho';
    case '5':
      return 'Mudança de Risco';
    default:
      return 'Admissional';
  }
};

export default function UploadLotePlanilha({ onImportar, onVoltar }: UploadLotePlanilhaProps) {
  const navigate = useNavigate();
  const { empresa } = useEmpresa();

  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidarPlanilhaResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [topStructureCollapsed, setTopStructureCollapsed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados de Pré-seleção de Clínicas
  const [estados, setEstados] = useState<EstadoItem[]>([]);
  const [cidades, setCidades] = useState<CidadeItem[]>([]);
  const [clinicasDisponiveis, setClinicasDisponiveis] = useState<ClinicaItem[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<string>('');
  const [selectedCidade, setSelectedCidade] = useState<string>('');
  const [selectedClinicaId, setSelectedClinicaId] = useState<number | ''>('');
  const [selectedClinicas, setSelectedClinicas] = useState<ClinicaItem[]>([]);
  const [loadingLocais, setLoadingLocais] = useState({
    estados: false,
    cidades: false,
    clinicas: false,
  });

  // Modal / Processamento do Fluxo Expresso Zero-Touch
  const [zeroTouchModalOpen, setZeroTouchModalOpen] = useState(false);
  const [zeroTouchStep, setZeroTouchStep] = useState<'loading' | 'success' | 'error'>('loading');
  const [zeroTouchProgress, setZeroTouchProgress] = useState(0);
  const [zeroTouchStatus, setZeroTouchStatus] = useState('');
  const [zeroTouchError, setZeroTouchError] = useState<string | null>(null);

  // Carregar estados ao montar
  useEffect(() => {
    let isMounted = true;
    const loadEstados = async () => {
      setLoadingLocais((prev) => ({ ...prev, estados: true }));
      try {
        const data = await clinicasService.getEstados();
        if (isMounted) setEstados(data || []);
      } catch (err) {
        console.error('Erro ao carregar estados:', err);
      } finally {
        if (isMounted) setLoadingLocais((prev) => ({ ...prev, estados: false }));
      }
    };
    loadEstados();
    return () => {
      isMounted = false;
    };
  }, []);

  // Carregar cidades quando o estado mudar
  useEffect(() => {
    let isMounted = true;
    if (!selectedEstado) {
      setCidades([]);
      setSelectedCidade('');
      setClinicasDisponiveis([]);
      setSelectedClinicaId('');
      return;
    }

    const loadCidades = async () => {
      setLoadingLocais((prev) => ({ ...prev, cidades: true }));
      try {
        const data = await clinicasService.getCidadesByEstado(selectedEstado);
        if (isMounted) {
          setCidades(data || []);
          setSelectedCidade('');
          setClinicasDisponiveis([]);
          setSelectedClinicaId('');
        }
      } catch (err) {
        console.error('Erro ao carregar cidades:', err);
      } finally {
        if (isMounted) setLoadingLocais((prev) => ({ ...prev, cidades: false }));
      }
    };
    loadCidades();
    return () => {
      isMounted = false;
    };
  }, [selectedEstado]);

  // Carregar clínicas quando cidade mudar
  useEffect(() => {
    let isMounted = true;
    if (!selectedEstado || !selectedCidade) {
      setClinicasDisponiveis([]);
      setSelectedClinicaId('');
      return;
    }

    const loadClinicas = async () => {
      setLoadingLocais((prev) => ({ ...prev, clinicas: true }));
      try {
        const data = await clinicasService.getClinicasFiltradas({
          estado: selectedEstado,
          cidade: selectedCidade,
          limit: 100,
        });
        if (isMounted) {
          setClinicasDisponiveis(data || []);
          setSelectedClinicaId('');
        }
      } catch (err) {
        console.error('Erro ao carregar clínicas:', err);
      } finally {
        if (isMounted) setLoadingLocais((prev) => ({ ...prev, clinicas: false }));
      }
    };
    loadClinicas();
    return () => {
      isMounted = false;
    };
  }, [selectedEstado, selectedCidade]);

  // Adicionar clínica na lista de pré-seleção
  const handleAddClinica = () => {
    if (!selectedClinicaId) return;
    const clinica = clinicasDisponiveis.find((c) => c.id === Number(selectedClinicaId));
    if (!clinica) return;

    if (selectedClinicas.some((c) => c.id === clinica.id)) {
      setSelectedClinicaId('');
      return;
    }

    setSelectedClinicas((prev) => [...prev, clinica]);
    setSelectedClinicaId('');
  };

  const handleRemoveClinica = (id: number) => {
    setSelectedClinicas((prev) => prev.filter((c) => c.id !== id));
  };

  const handleClearClinicas = () => {
    setSelectedClinicas([]);
  };

  // Download do Modelo Excel com clínicas selecionadas
  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      setErrorMessage(null);
      const clinicaIds = selectedClinicas.length > 0 ? selectedClinicas.map((c) => c.id) : undefined;
      await agendamentosService.downloadTemplatePlanilha(clinicaIds, 'completo');
    } catch (err: unknown) {
      console.error('Erro ao baixar modelo:', err);
      setErrorMessage('Erro ao baixar a planilha modelo. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  // Processamento e leitura de múltiplas abas
  const processFile = async (file: File) => {
    const securityCheck = await validateExcelFile(file);
    if (!securityCheck.valid) {
      setErrorMessage(securityCheck.error || 'Arquivo inválido.');
      return;
    }

    try {
      setUploading(true);
      setErrorMessage(null);
      setFileName(file.name);

      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Filtrar abas de apoio / suporte
      const nonSupportSheets = workbook.SheetNames.filter((name) => {
        const lower = name.toLowerCase();
        return (
          !lower.includes('apoio') &&
          !lower.includes('instru') &&
          !lower.includes('legenda') &&
          !lower.includes('ajuda')
        );
      });

      if (nonSupportSheets.length === 0) {
        setErrorMessage('Nenhuma aba de dados encontrada na planilha. Utilize a planilha modelo oficial.');
        setUploading(false);
        return;
      }

      const allRawRows: Record<string, unknown>[] = [];

      for (const sheetName of nonSupportSheets) {
        const worksheet = workbook.Sheets[sheetName];
        const sheetRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        const lowerName = sheetName.toLowerCase();
        const origemAba: 'admissional' | 'existentes' =
          lowerName.includes('exist') || lowerName.includes('demiss') || lowerName.includes('period')
            ? 'existentes'
            : 'admissional';

        for (const row of sheetRows) {
          const rawCpf = String(row['CPF (*)'] || row['CPF'] || '').trim();
          const rawNome = String(row['Nome Completo (*)'] || row['Nome Completo'] || row['Nome'] || '').trim();
          // Ignorar linhas vazias ou de formatação sem CPF e sem Nome
          if (!rawCpf && !rawNome) {
            continue;
          }
          allRawRows.push({
            ...row,
            _origem_aba: origemAba,
          });
        }
      }

      if (allRawRows.length === 0) {
        setErrorMessage('A planilha enviada está vazia ou não contém colaboradores preenchidos. Preencha as informações conforme o modelo.');
        setUploading(false);
        return;
      }

      // Higienização contra formula injection
      const sanitizedRows = sanitizeSpreadsheetRows(allRawRows);

      // Validação completa na API
      const result = await agendamentosService.validarPlanilha(sanitizedRows);
      setValidationResult(result);
      setTopStructureCollapsed(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string | string[] } } };
      console.error('Erro ao processar planilha:', err);
      const rawMsg = error?.response?.data?.message;
      let displayMsg = 'Erro ao validar o arquivo Excel. Verifique a estrutura.';
      if (Array.isArray(rawMsg)) {
        displayMsg = rawMsg.join('\n');
      } else if (typeof rawMsg === 'string' && rawMsg.trim()) {
        displayMsg = rawMsg;
      }
      setErrorMessage(displayMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleConfirmarImportacao = () => {
    if (!validationResult || validationResult.validRows.length === 0) return;
    onImportar(validationResult.validRows);
  };

  // Contadores para o fluxo Zero-Touch
  const completos = useMemo(() => {
    return validationResult?.validRows.filter((r) => r.is_completo_para_emissao) || [];
  }, [validationResult]);

  const completosCount = completos.length;

  // Execução do Fluxo Expresso Zero-Touch
  const handleZeroTouchEmitir = async () => {
    if (!validationResult || completos.length === 0) return;

    setZeroTouchModalOpen(true);
    setZeroTouchStep('loading');
    setZeroTouchProgress(15);
    setZeroTouchStatus('Iniciando processamento seguro das guias...');
    setZeroTouchError(null);

    try {
      setZeroTouchStatus('Cadastrando colaboradores novos e validando cargos/setores...');
      setZeroTouchProgress(35);

      const rowsWithColabId: { row: ValidarPlanilhaRowContract; colabId: number }[] = [];

      for (let i = 0; i < completos.length; i++) {
        const row = completos[i];
        let colabId = row.colaborador_id ? Number(row.colaborador_id) : undefined;

        if (!colabId) {
          const cleanCpf = String(row.cpf || '').replace(/\D/g, '');
          const nasc = formatDateForAPI(row.nascimento) || '2000-01-01';

          const createData: CreateColaboradorDto = {
            empresa_id: empresa?.id,
            nome: String(row.nome || '').trim(),
            cpf: cleanCpf,
            data_nascimento: nasc,
            sexo: ((row.sexo as string) || 'M').toUpperCase() as 'M' | 'F',
            telefone: row.telefone && String(row.telefone).trim() !== '' ? String(row.telefone).trim() : undefined,
            email: row.email && String(row.email).trim() !== '' ? String(row.email).trim() : undefined,
            is_candidato: Boolean(row.is_candidato),
            cargo_id: row.cargo_id ? Number(row.cargo_id) : undefined,
            setor_id: row.setor_id ? Number(row.setor_id) : undefined,
            cargo_nome: String(row.cargo || '').trim(),
            setor_nome: String(row.setor || '').trim(),
            matricula_esocial: row.matricula_esocial ? String(row.matricula_esocial).trim() : undefined,
            nome_social: row.nome_social ? String(row.nome_social).trim() : undefined,
            pcd: row.pcd !== undefined ? Boolean(row.pcd) : undefined,
            enviar_esocial: row.enviar_esocial !== undefined ? Boolean(row.enviar_esocial) : undefined,
            regime_contratacao: (row.regime_contratacao || row.tipo_contrato) ? String(row.regime_contratacao || row.tipo_contrato) : undefined,
            estado_civil: row.estado_civil ? String(row.estado_civil) : undefined,
          };

          if (row.data_admissao) {
            const adm = formatDateForAPI(String(row.data_admissao));
            if (adm) createData.data_admissao = adm;
          }
          if (row.data_demissao) {
            const dem = formatDateForAPI(String(row.data_demissao));
            if (dem) createData.data_demissao = dem;
          }

          const novoColab = await colaboradoresService.create(createData);
          if (!novoColab?.id) {
            throw new Error(`Falha ao registrar colaborador ${row.nome}.`);
          }
          colabId = novoColab.id;
        } else {
          // Se for demissional e tiver data_demissao, sincronizar
          if (Number(row.exame) === 2 && row.data_demissao) {
            const dem = formatDateForAPI(String(row.data_demissao));
            if (dem) {
              await colaboradoresService.update(colabId, { data_demissao: dem }).catch(() => {});
            }
          }
        }

        rowsWithColabId.push({ row, colabId });
        const stepProgress = 35 + Math.round(((i + 1) / completos.length) * 35);
        setZeroTouchProgress(Math.min(stepProgress, 70));
      }

      setZeroTouchStatus('Consolidando exames complementares dos cargos...');
      setZeroTouchProgress(75);

      const rowsWithExames = await Promise.all(
        rowsWithColabId.map(async (item) => {
          let examesClinicosIds: number[] = [];
          if (item.row.cargo_id) {
            try {
              const res = await cargosService.getExamesByCargo(
                Number(item.row.cargo_id),
                empresa?.id,
                item.row.setor_id ? Number(item.row.setor_id) : undefined,
                item.row.exame,
              );
              if (Array.isArray(res?.exames_clinicos_ids)) {
                examesClinicosIds = res.exames_clinicos_ids;
              }
            } catch (err) {
              console.warn(
                `[ZeroTouch] Falha ao consultar exames do cargo #${item.row.cargo_id}:`,
                err,
              );
            }
          }
          return { ...item, examesClinicosIds };
        }),
      );

      setZeroTouchStatus('Gerando ordens de atendimento e consolidando guias...');
      setZeroTouchProgress(85);

      const agendamentosPayload = rowsWithExames.map((item) => {
        const isColabLink = item.row.escolha_clinica_tipo === 'colaborador';
        return {
          colaborador_id: item.colabId,
          clinica_id: isColabLink ? undefined : item.row.clinica_id ? Number(item.row.clinica_id) : undefined,
          variacao_id: 1,
          exame_id: Number(item.row.exame || 1),
          data_agendamento:
            !isColabLink && item.row.data_agendamento
              ? formatDateForAPI(String(item.row.data_agendamento))
              : undefined,
          exames_clinicos_ids: item.examesClinicosIds,
          online: 0,
          escolha_clinica_tipo: isColabLink ? ('colaborador' as const) : ('rh' as const),
        };
      });

      const response = await agendamentosService.create({
        agendamentos: agendamentosPayload,
        tipo_pagamento: 'faturamento',
      });

      setZeroTouchProgress(100);
      setZeroTouchStatus('Guias emitidas com sucesso! Redirecionando...');
      setZeroTouchStep('success');

      // Limpar rascunhos de agendamento anteriores
      limparRascunhoAgendamento();

      setTimeout(() => {
        navigate(`/agendamento/final/${response.codigo_pedido}`);
      }, 1000);
    } catch (err: unknown) {
      console.error('Erro na emissão expressa:', err);
      const error = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const msg = error?.response?.data?.message || error?.message || 'Erro ao emitir guias expressas.';
      setZeroTouchError(Array.isArray(msg) ? msg.join(', ') : msg);
      setZeroTouchStep('error');
    }
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Barra Compacta Superior com Animação quando recolhido */}
      {validationResult && topStructureCollapsed && (
        <div className="bg-gradient-to-r from-emerald-50 via-teal-50/70 to-white rounded-2xl border border-emerald-200/90 p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all duration-300">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white flex items-center justify-center shadow-md shrink-0">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base font-bold text-gray-900">{fileName || 'Planilha Processada'}</span>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                  {validationResult.totalRows} {validationResult.totalRows === 1 ? 'colaborador' : 'colaboradores'}
                </span>
                {validationResult.errorsCount > 0 ? (
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300">
                    {validationResult.errorsCount} inconsistência(s)
                  </span>
                ) : (
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                    100% Válido
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Estrutura de instruções e upload recolhida para facilitar a visualização do resultado abaixo.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setTopStructureCollapsed(false)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-emerald-50 text-emerald-800 border border-emerald-300 font-semibold rounded-xl text-xs shadow-sm transition-all hover:scale-[1.02] active:scale-95"
            >
              <RotateCcw className="w-4 h-4 text-emerald-600" />
              <span>Reabrir Instruções / Trocar Planilha</span>
            </button>
          </div>
        </div>
      )}

      {/* Estrutura Superior (Instruções, Pré-seleção de Clínicas e Upload) */}
      <div
        className={`transition-all duration-700 ease-in-out origin-top ${
          topStructureCollapsed && validationResult
            ? 'max-h-0 opacity-0 overflow-hidden pointer-events-none -translate-y-4 scale-98 my-0 py-0'
            : 'max-h-[3500px] opacity-100 overflow-visible translate-y-0 scale-100 space-y-6'
        }`}
      >
        {/* Header com Ações e Pré-seleção */}
      <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 sm:p-8 space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
              Agendamento em Lote via Planilha
            </span>
            <h2 className="text-2xl font-bold text-gray-900 mt-2">
              Importação Inteligente & Emissão Expressa
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl leading-relaxed">
              Baixe a planilha oficial com seus cargos, setores e colaboradores ativos. Você pode
              pré-selecionar as clínicas desejadas abaixo para saírem prontas na lista suspensa do
              Excel!
            </p>
          </div>

          <div className="flex items-center shrink-0">
            <button
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="flex items-center justify-center gap-2.5 px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-md transition-all transform active:scale-95 shrink-0"
            >
              {downloading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span>Baixar Planilha Modelo (.xlsx)</span>
            </button>
          </div>
        </div>

        {/* ========================================================= */}
        {/* PRÉ-SELEÇÃO DE CLÍNICAS PARA A PLANILHA                    */}
        {/* ========================================================= */}
        <div className="relative bg-gradient-to-br from-emerald-50/60 to-gray-50/70 rounded-xl border border-emerald-200/80 p-5 space-y-4">
          {/* Triângulo / Setinha física na borda superior apontando para o botão Baixar Modelo */}
          <div className="hidden sm:block absolute -top-2.5 right-24 sm:right-28 w-5 h-5 bg-[#ebfbf3] border-t border-l border-emerald-300 transform rotate-45 z-10" />

          {/* Badge flutuante com setinha animada apontando para cima */}
          <div className="hidden sm:flex items-center gap-1.5 absolute -top-3.5 right-8 sm:right-16 bg-emerald-700 text-white text-[11px] font-bold px-3 py-0.5 rounded-full shadow-md border border-emerald-600 z-20 animate-pulse">
            <ArrowUp className="w-3.5 h-3.5 text-emerald-200" />
            <span>Suas clínicas vão para a planilha baixada acima</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-emerald-900 font-bold text-sm">
              <Building2 className="w-4 h-4 text-emerald-700" />
              <span>Já sabe as clínicas que deseja utilizar? Pré-selecione aqui:</span>
              <span className="sm:hidden inline-flex items-center gap-1 text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-300">
                <ArrowUp className="w-3 h-3 text-emerald-700" /> Vai para o modelo acima
              </span>
            </div>
            {selectedClinicas.length > 0 && (
              <button
                type="button"
                onClick={handleClearClinicas}
                className="text-xs font-medium text-gray-500 hover:text-red-600 underline"
              >
                Limpar seleção ({selectedClinicas.length})
              </button>
            )}
          </div>

          <p className="text-xs text-gray-600">
            As clínicas escolhidas abaixo serão incluídas na coluna de seleção rápida do arquivo
            Excel, permitindo que você apenas escolha a clínica no dropdown de cada linha.
          </p>

          {/* Filtros em Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-3">
              <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                Estado (UF)
              </label>
              <select
                value={selectedEstado}
                onChange={(e) => setSelectedEstado(e.target.value)}
                disabled={loadingLocais.estados}
                className="w-full text-xs bg-white border border-gray-200 rounded-lg p-2.5 text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              >
                <option value="">Selecione o Estado</option>
                {estados.map((uf) => (
                  <option key={uf.uf} value={uf.uf}>
                    {uf.nome} ({uf.uf})
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-4">
              <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                Cidade
              </label>
              <select
                value={selectedCidade}
                onChange={(e) => setSelectedCidade(e.target.value)}
                disabled={!selectedEstado || loadingLocais.cidades}
                className="w-full text-xs bg-white border border-gray-200 rounded-lg p-2.5 text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">
                  {loadingLocais.cidades ? 'Carregando cidades...' : 'Selecione a Cidade'}
                </option>
                {cidades.map((cid, i) => (
                  <option key={i} value={cid.nome}>
                    {cid.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-4">
              <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                Clínica Conveniada
              </label>
              <select
                value={selectedClinicaId}
                onChange={(e) => setSelectedClinicaId(e.target.value ? Number(e.target.value) : '')}
                disabled={!selectedCidade || loadingLocais.clinicas}
                className="w-full text-xs bg-white border border-gray-200 rounded-lg p-2.5 text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">
                  {loadingLocais.clinicas
                    ? 'Buscando clínicas...'
                    : clinicasDisponiveis.length === 0
                      ? 'Nenhuma clínica nesta cidade'
                      : 'Selecione a Clínica'}
                </option>
                {clinicasDisponiveis.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome} {c.bairro ? `(${c.bairro})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-1">
              <button
                type="button"
                onClick={handleAddClinica}
                disabled={!selectedClinicaId}
                title="Adicionar à lista da planilha"
                className="w-full flex items-center justify-center p-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Chips das Clínicas Adicionadas */}
          {selectedClinicas.length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-2">
              {selectedClinicas.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-emerald-800 rounded-lg text-xs font-semibold border border-emerald-300 shadow-sm"
                >
                  <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>
                    [{c.uf}] {c.nome} {c.cidade ? `- ${c.cidade}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveClinica(c.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors ml-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500 italic">
              Nenhuma clínica pré-selecionada. (O modelo virá com as principais clínicas conveniadas
              ativas no sistema).
            </div>
          )}
        </div>

        {/* 3 Pilares e Dicas de Preenchimento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
          <div className="bg-gray-50/80 p-4 rounded-xl border border-gray-200/70">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold text-xs mb-1.5">
              <Users className="w-4 h-4" />
              1. Aba Existentes Pré-preenchida
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              A aba <strong>Existentes</strong> já lista seus colaboradores ativos com CPF e matrícula.
              Basta selecionar o tipo de exame, clínica e data.
            </p>
          </div>

          <div className="bg-gray-50/80 p-4 rounded-xl border border-gray-200/70">
            <div className="flex items-center gap-2 text-blue-700 font-semibold text-xs mb-1.5">
              <Calendar className="w-4 h-4" />
              2. Data do Exame & Demissão
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Informe a <strong>Data Prevista</strong> do exame no formato DD/MM/AAAA. Para exames
              demissionais, preencha também a <strong>Data de Demissão</strong>.
            </p>
          </div>

          <div className="bg-gray-50/80 p-4 rounded-xl border border-gray-200/70">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold text-xs mb-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              3. Emissão Direta de Guias
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Se as linhas já contiverem clínica e data (ou link do colaborador), o sistema emite as
              guias diretamente sem etapas manuais intermediárias!
            </p>
          </div>
        </div>
      </div>

      {/* Mensagem de Erro Geral */}
      {errorMessage && (
        <div className="p-4 rounded-xl bg-red-50 text-red-800 border border-red-200 flex items-start gap-3 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          <div className="whitespace-pre-line break-words flex-1 leading-relaxed">{errorMessage}</div>
        </div>
      )}

      {/* Drag and Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`bg-white border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
          dragActive
            ? 'border-emerald-500 bg-emerald-50/30 ring-4 ring-emerald-500/10'
            : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
          {uploading ? (
            <RefreshCw className="w-8 h-8 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-8 h-8" />
          )}
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-1">
          {fileName ? fileName : 'Arraste e solte sua planilha preenchida aqui'}
        </h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          ou clique para selecionar o arquivo no seu computador (formatos .xlsx ou .xls)
        </p>

        <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200">
          <Upload className="w-3.5 h-3.5" />
          Selecionar Arquivo
        </div>
      </div>

      {/* Botão sutil para recolher painel quando estiver aberto e com resultados */}
      {validationResult && !topStructureCollapsed && (
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={() => setTopStructureCollapsed(true)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-emerald-700 font-medium py-1.5 px-3 rounded-lg hover:bg-emerald-50 transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
            <span>Recolher instruções e upload</span>
          </button>
        </div>
      )}
    </div>

      {/* ========================================================= */}
      {/* RESULTADO DA VALIDAÇÃO & FLUXOS DE AÇÃO                   */}
      {/* ========================================================= */}
      {validationResult && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
          {/* Métricas */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Resultado da Validação</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Total de linhas: {validationResult.totalRows} | Válidas:{' '}
                <strong className="text-emerald-600">{validationResult.validRowsCount}</strong> |
                Prontas para Emissão Direta:{' '}
                <strong className="text-purple-600">{completosCount}</strong> | Com inconsistência:{' '}
                <strong className={validationResult.errorsCount > 0 ? 'text-red-600' : 'text-gray-600'}>
                  {validationResult.errorsCount}
                </strong>
              </p>
            </div>
          </div>

          {/* ========================================================= */}
          {/* BANNER DE EMISSÃO DIRETA DE GUIAS                         */}
          {/* ========================================================= */}
          {completosCount > 0 && (
            <div className="p-6 bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 text-white rounded-2xl shadow-lg border border-emerald-500/30 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-1.5">
                <h4 className="text-xl font-bold text-white">
                  Emitir Guias Diretamente ({completosCount} colaboradores)
                </h4>
                <p className="text-xs text-emerald-100/80 max-w-xl leading-relaxed">
                  Identificamos que {completosCount} colaborador(es) já possuem todas as informações
                  necessárias (clínicas, datas e vínculos definidos na planilha). Você pode emitir as
                  guias agora sem passar pelas etapas manuais intermediárias!
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                <button
                  type="button"
                  onClick={handleZeroTouchEmitir}
                  className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-gray-950 font-bold rounded-xl text-sm shadow-md transition-all transform active:scale-95 shrink-0"
                >
                  <span>Emitir Guias Agora</span>
                </button>

                <button
                  type="button"
                  onClick={handleConfirmarImportacao}
                  className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold border border-white/20 transition-colors"
                >
                  Revisar Cadastros
                </button>
              </div>
            </div>
          )}

          {/* Ação padrão se não houver completos ou se o usuário quiser continuar */}
          {completosCount === 0 && validationResult.validRowsCount > 0 && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-xs text-gray-600">
                Colaboradores validados com sucesso. Avance para selecionar as clínicas e datas no
                assistente passo a passo.
              </div>
              <button
                onClick={handleConfirmarImportacao}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-sm shadow-md transition-all active:scale-95 shrink-0"
              >
                <span>Avançar com {validationResult.validRowsCount} Colaboradores</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Banner de Execução via Worker (> 50 linhas) */}
          {validationResult.shouldQueue && (
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-3 text-xs text-indigo-950">
              <Sparkles className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-sm text-indigo-900">
                  Processamento em Lote Otimizado Ativado
                </p>
                <p className="text-indigo-700 mt-1 leading-relaxed">
                  Como a planilha possui mais de 50 colaboradores (
                  <strong>{validationResult.totalRows} registros</strong>), o sistema processará de forma
                  assíncrona para prevenir timeouts e garantir a integridade dos agendamentos.
                </p>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* RESUMO DETALHADO DE ERROS / INCONSISTÊNCIAS               */}
          {/* ========================================================= */}
          {validationResult.errors.length > 0 && (
            <div className="rounded-2xl bg-rose-50/90 border border-rose-200 p-5 space-y-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-rose-200/80">
                <div className="flex items-center gap-2.5 text-rose-900 font-bold text-sm">
                  <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-rose-900">
                      Resumo de Inconsistências Detectadas
                    </h4>
                    <p className="text-xs text-rose-700 font-normal">
                      Foram encontradas {validationResult.errors.length} inconsistência(s) que impedem o processamento dessas linhas.
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-rose-200 text-rose-800 border border-rose-300 shrink-0">
                  {validationResult.errors.length} erro(s) identificado(s)
                </span>
              </div>

              {/* Grid de Erros com Badges Claras */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-72 overflow-y-auto pr-1">
                {validationResult.errors.map((err, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-white rounded-xl border border-rose-200 shadow-2xs flex items-start gap-2.5 text-xs text-gray-800"
                  >
                    <div className="mt-0.5 shrink-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                        Linha #{err.rowNumber}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-rose-900 block mb-0.5">
                        {err.col}:
                      </span>
                      <p className="text-gray-700 leading-relaxed break-words">{err.message}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dica de Ação */}
              <div className="flex items-start sm:items-center gap-2 pt-1 text-xs text-rose-800 bg-rose-100/60 p-3 rounded-xl border border-rose-200/60">
                <Sparkles className="w-4 h-4 text-rose-600 shrink-0 mt-0.5 sm:mt-0" />
                <span>
                  <strong>Como resolver:</strong> Você pode corrigir os dados no seu arquivo Excel e clicar em <strong>"Reabrir Instruções / Trocar Planilha"</strong> para reenviar, ou prosseguir normalmente com as <strong>{validationResult.validRowsCount} linhas válidas</strong>.
                </span>
              </div>
            </div>
          )}

          {/* Tabela de Preview dos Válidos */}
          {validationResult.validRows.length > 0 ? (
            <div className="overflow-x-auto border border-gray-100 rounded-xl">
              <table className="w-full text-left text-xs text-gray-600">
                <thead className="bg-gray-50 text-gray-500 font-semibold uppercase">
                  <tr>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3">Origem</th>
                    <th className="py-3 px-3">Exame</th>
                    <th className="py-3 px-3">Nome</th>
                    <th className="py-3 px-3">CPF</th>
                    <th className="py-3 px-3">Cargo / Setor</th>
                    <th className="py-3 px-3">Clínica Preenchida</th>
                    <th className="py-3 px-3">Data Prevista</th>
                    <th className="py-3 px-3">Escolha Clínica</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {validationResult.validRows.map((colab, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className="py-2.5 px-3">
                        {colab.is_completo_para_emissao ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded text-[11px] border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            Emissão Direta
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium bg-emerald-50 px-2 py-0.5 rounded text-[11px]">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            Válido
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="bg-gray-100 text-gray-700 text-[10px] font-semibold px-2 py-0.5 rounded">
                          {colab.origem_aba === 'existentes' ? 'Ativo' : 'Admissional'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-gray-900">
                        {getExameNome(colab.exame)}
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-gray-900">{colab.nome}</td>
                      <td className="py-2.5 px-3 font-mono font-medium text-gray-800">{formatCPF(colab.cpf)}</td>
                      <td className="py-2.5 px-3">
                        <div className="font-medium text-gray-800">{colab.cargo}</div>
                        <div className="text-[10px] text-gray-500">{colab.setor}</div>
                      </td>
                      <td className="py-2.5 px-3">
                        {colab.clinica_nome ? (
                          <span className="text-gray-800 font-medium">
                            {colab.clinica_nome}
                          </span>
                        ) : colab.escolha_clinica_tipo === 'colaborador' ? (
                          <span className="text-blue-600 font-medium">Via link colaborador</span>
                        ) : (
                          <span className="text-gray-400 italic">Não definida</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        {colab.data_agendamento ? (
                          <span className="inline-flex items-center gap-1.5 text-gray-900 font-semibold bg-gray-50 px-2 py-1 rounded border border-gray-200">
                            <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                            {formatDisplayDate(colab.data_agendamento)}
                          </span>
                        ) : colab.data_demissao ? (
                          <span className="inline-flex items-center gap-1 text-red-700 font-semibold text-[11px] bg-red-50 px-2 py-0.5 rounded border border-red-200">
                            <Calendar className="w-3 h-3 text-red-500" />
                            Demissão: {formatDisplayDate(colab.data_demissao)}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">-</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        {colab.escolha_clinica_tipo === 'colaborador' ? (
                          <span className="bg-blue-50 text-blue-700 text-[10px] font-semibold px-2 py-0.5 rounded border border-blue-100">
                            Link Colaborador
                          </span>
                        ) : (
                          <span className="bg-gray-100 text-gray-700 text-[10px] font-semibold px-2 py-0.5 rounded">
                            RH
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 text-xs">
              Nenhuma linha válida encontrada para importação. Corrija a planilha e envie novamente.
            </div>
          )}
        </div>
      )}

      {/* Botão de Voltar */}
      <div className="flex justify-between items-center pt-4">
        <button
          onClick={onVoltar}
          className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Voltar para Opções de Agendamento
        </button>
      </div>

      {/* ========================================================= */}
      {/* MODAL DE EXECUÇÃO DO FLUXO EXPRESSO ZERO-TOUCH             */}
      {/* ========================================================= */}
      {zeroTouchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 max-w-lg w-full space-y-6">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center bg-gradient-to-tr from-emerald-500 to-teal-600 text-white shadow-lg">
                {zeroTouchStep === 'loading' ? (
                  <Loader2 className="w-7 h-7 animate-spin text-white" />
                ) : zeroTouchStep === 'success' ? (
                  <CheckCircle2 className="w-8 h-8 text-white" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {zeroTouchStep === 'loading' && 'Emitindo Guias em Lote'}
                {zeroTouchStep === 'success' && 'Guias Emitidas com Sucesso!'}
                {zeroTouchStep === 'error' && 'Inconsistência na Emissão'}
              </h3>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                {zeroTouchStep === 'loading' &&
                  'Aguarde enquanto os registros são sincronizados e as guias corporativas são geradas de forma segura.'}
                {zeroTouchStep === 'success' &&
                  'Todos os agendamentos foram criados no contrato da empresa. Redirecionando para as guias...'}
                {zeroTouchStep === 'error' &&
                  'Não foi possível concluir a emissão direta automaticamente. Você pode revisar os cadastros passo a passo.'}
              </p>
            </div>

            {/* Barra de Progresso */}
            {zeroTouchStep === 'loading' && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-semibold text-gray-600">
                  <span>{zeroTouchStatus}</span>
                  <span>{zeroTouchProgress}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${zeroTouchProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Mensagem de Erro se houver */}
            {zeroTouchStep === 'error' && zeroTouchError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-xs text-red-800 space-y-2">
                <p className="font-bold flex items-center gap-1">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                  Detalhes do erro:
                </p>
                <p className="leading-relaxed">{zeroTouchError}</p>
              </div>
            )}

            {/* Ações no Modal em caso de erro */}
            {zeroTouchStep === 'error' && (
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleZeroTouchEmitir}
                  className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs transition-colors"
                >
                  Tentar Novamente
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setZeroTouchModalOpen(false);
                    handleConfirmarImportacao();
                  }}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-xs transition-colors"
                >
                  Revisar Cadastros
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
